import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import LegalFooter from "@/components/LegalFooter";

export default function PortalRegister() {
  const { clientRegister } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ portal_code: "", name: "", email: "", password: "" });
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.portal_code.trim().length < 6) { setError("Enter the portal code from your invite."); return; }
    if (form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (!accepted) { setError("Please accept the Terms of Use and Privacy Policy to continue."); return; }
    setLoading(true);
    try {
      await clientRegister({
        portal_code: form.portal_code.trim(),
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        accept_legal: true,
      });
      navigate("/portal", { replace: true });
    } catch (err) {
      setError(apiError(err, "Could not create your account"));
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><ShieldCheck className="h-6 w-6" /></div>
          <h1 className="text-2xl font-bold text-slate-900">Create your portal account</h1>
          <p className="mt-1 text-sm text-slate-500">Use the portal code from your invite.</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Portal code</Label>
            <Input value={form.portal_code} onChange={(e) => set("portal_code", e.target.value)} placeholder="e.g. ABC123" data-testid="register-portal-code" />
          </div>
          <div className="space-y-1.5">
            <Label>Full name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="register-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="register-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Password</Label>
            <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="register-password" />
          </div>
          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="register-error">{error}</p>}
          <label className="flex items-start gap-2.5 text-sm text-slate-600" data-testid="portal-register-accept-label">
            <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="mt-0.5" data-testid="portal-register-accept-legal" />
            <span>
              I agree to the{" "}
              <Link to="/legal/terms" target="_blank" className="font-medium text-primary hover:underline">Terms of Use</Link>{" "}
              and{" "}
              <Link to="/legal/privacy" target="_blank" className="font-medium text-primary hover:underline">Privacy Policy</Link>.
            </span>
          </label>
          <Button type="submit" className="w-full" size="lg" disabled={loading} data-testid="register-submit">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Create account
          </Button>
        </form>
        <p className="mt-4 text-center text-sm text-slate-500">
          Already have an account? <Link to="/portal/login" className="font-medium text-primary hover:underline">Sign in</Link>
        </p>
        <LegalFooter className="mt-5" />
      </div>
    </div>
  );
}
